import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";

import { EMPTY_GUID, RECORD_TYPE, ChargeProfileRoutes } from "../../../../const/constants";
import PrimaryTab from "./tabs/PrimaryTab";
import UserDefinedTab from "./tabs/UserDefinedTab";
import AccountingTab from "./tabs/AccountingTab";
import StorageTab from "./tabs/StorageTab";
import BillingTab from "./tabs/BillingTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const formFieldsRef = useRef(null);
    const hasSeededProfileDefaults = useRef(false);

    // Single source of truth for the profile-defaults route (Charge Profile -> Charges Defaults).
    // Read from the live URL, not props: the field-change and form-load closures are captured before
    // props are populated, so a props-derived value goes stale exactly where it matters.
    // formFieldsRef is the second source because an existing 'P' record opened for edit carries the
    // record type in state without recordType being in the URL. Mirrors itemDataEntry/Form.js.
    const readProfileDefaults = () => {
        const params = new URLSearchParams(window.location.search);
        return {
            isProfileDefaults:
                params.get('recordType') === RECORD_TYPE.PROFILE_DEFAULTS
                || formFieldsRef.current?.RECORDTYPE === RECORD_TYPE.PROFILE_DEFAULTS,
            profileRowId: params.get('RProfile'),
            profileCode: params.get('profileCode'),
        };
    };

    const { isProfileDefaults } = readProfileDefaults();
    const initialFormFields = {
        ROWID: '00000000-0000-0000-0000-000000000000',
        RECORD: '',
        // 'P' on the Charges Defaults route, otherwise left for the API to stamp as 'R'.
        RECORDTYPE: readProfileDefaults().isProfileDefaults ? RECORD_TYPE.PROFILE_DEFAULTS : '',
        R_PROFILE: '',
        CHARGECODE: '',
        DESCRIPTION1: '',
        DESCRIPTION2: '',
        CHARGETYPE: 'I',
        MANAGEMENTSOURCE: 'C',
        revenueGroup: '',
        COMMENT: '',
        USERDEFINEDCODE_1: '',
        USERDEFINEDCODE_2: '',
        USERDEFINEDCODE_3: '',
        USERDEFINEDCODE_4: '',
        USERDEFINEDCODE_5: '',
        USERDEFINEDCODE_6: '',
        USERDEFINEDCODE_7: '',
        USERDEFINEDCODE_8: '',
        USERDEFINEDCODE_9: '',
        USERDEFINEDCODE_10: '',
        USERDEFINEDDATE_1: null,
        USERDEFINEDDATE_2: null,
        USERDEFINEDDATE_3: null,
        USERDEFINEDDATE_4: null,
        USERDEFINEDDATE_5: null,
        USERDEFINEDDATE_6: null,
        USERDEFINEDDATE_7: null,
        USERDEFINEDDATE_8: null,
        USERDEFINEDDATE_9: null,
        USERDEFINEDDATE_10: null,
        USERDEFINEDREFERENCE_1: '',
        USERDEFINEDREFERENCE_2: '',
        USERDEFINEDREFERENCE_3: '',
        USERDEFINEDREFERENCE_4: '',
        USERDEFINEDREFERENCE_5: '',
        USERDEFINEDREFERENCE_6: '',
        USERDEFINEDREFERENCE_7: '',
        USERDEFINEDREFERENCE_8: '',
        USERDEFINEDREFERENCE_9: '',
        USERDEFINEDREFERENCE_10: '',
        USERDEFINEDQUANTITY_1: null,
        USERDEFINEDQUANTITY_2: null,
        USERDEFINEDQUANTITY_3: null,
        USERDEFINEDQUANTITY_4: null,
        USERDEFINEDQUANTITY_5: null,
        USERDEFINEDQUANTITY_6: null,
        USERDEFINEDQUANTITY_7: null,
        USERDEFINEDQUANTITY_8: null,
        USERDEFINEDQUANTITY_9: null,
        USERDEFINEDQUANTITY_10: null,
        USERDEFINEDCOMMENT: '',
        R_GLACCOUNT: null,
        R_GLDEPARTMENT: null,
        R_FAFUND: null,
        R_CAJOB: null,
        R_CAACTIVITY: null,
        STORAGETIMING: '',
        STORAGEFREQUENCY: '',
        STATUS: 'A',
        TAXABLE: 'N',
        DISCOUNTABLE: 'N',
        BILLINGMETHOD: 'I',
        CHARGEBASISUNITS: 'I',
        ROUNDING: 'N',
        ACCESSBILLAS: 'Y',
        BILLASMINIMUMQUANTITY: null,
        CHARGEBASISLEVEL: 'I',
        CHARGEUOM: null,
        RATESOURCE: 'S',
        UNITCHARGERATE: null,
        R_RATETABLE: null,
        RATETABLEQUANTITYBASIS: 'I',
        CUMULATIVERATE: '',
        UNITCHARGEFACTOR: null,
        MINIMUMCHARGEAMOUNT: null,
        MAXIMUMCHARGEAMOUNT: null
    };
    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const loadProfileDefaults = useCallback(async (profileCode) => {
        if (!profileCode || profileCode === '00000000-0000-0000-0000-000000000000') {
            return;
        }

        try {
            const url = `${window._env_.REACT_APP_APIS}/${data.ChargeCode.GetChargeCodeProfileDefaults}?profileCode=${profileCode}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                const profileDefaults = response.data;
                setformFields(prevFields => ({
                    ...prevFields,
                    // Load profile defaults - overwrite existing values
                    DESCRIPTION1: profileDefaults.Description1 || prevFields.DESCRIPTION1 || '',
                    DESCRIPTION2: profileDefaults.Description2 || prevFields.DESCRIPTION2 || '',
                    CHARGETYPE: profileDefaults.ChargeType || prevFields.CHARGETYPE || 'I',
                    revenueGroup: profileDefaults.RevenueGroup || prevFields.revenueGroup || '',
                    COMMENT: profileDefaults.Comment || prevFields.COMMENT || '',
                    USERDEFINEDCODE_1: profileDefaults.UserDefinedCode1 || prevFields.USERDEFINEDCODE_1 || '',
                    USERDEFINEDCODE_2: profileDefaults.UserDefinedCode2 || prevFields.USERDEFINEDCODE_2 || '',
                    USERDEFINEDCODE_3: profileDefaults.UserDefinedCode3 || prevFields.USERDEFINEDCODE_3 || '',
                    USERDEFINEDCODE_4: profileDefaults.UserDefinedCode4 || prevFields.USERDEFINEDCODE_4 || '',
                    USERDEFINEDCODE_5: profileDefaults.UserDefinedCode5 || prevFields.USERDEFINEDCODE_5 || '',
                    USERDEFINEDCODE_6: profileDefaults.UserDefinedCode6 || prevFields.USERDEFINEDCODE_6 || '',
                    USERDEFINEDCODE_7: profileDefaults.UserDefinedCode7 || prevFields.USERDEFINEDCODE_7 || '',
                    USERDEFINEDCODE_8: profileDefaults.UserDefinedCode8 || prevFields.USERDEFINEDCODE_8 || '',
                    USERDEFINEDCODE_9: profileDefaults.UserDefinedCode9 || prevFields.USERDEFINEDCODE_9 || '',
                    USERDEFINEDCODE_10: profileDefaults.UserDefinedCode10 || prevFields.USERDEFINEDCODE_10 || '',
                    USERDEFINEDDATE_1: profileDefaults.UserDefinedDate1 || prevFields.USERDEFINEDDATE_1 || null,
                    USERDEFINEDDATE_2: profileDefaults.UserDefinedDate2 || prevFields.USERDEFINEDDATE_2 || null,
                    USERDEFINEDDATE_3: profileDefaults.UserDefinedDate3 || prevFields.USERDEFINEDDATE_3 || null,
                    USERDEFINEDDATE_4: profileDefaults.UserDefinedDate4 || prevFields.USERDEFINEDDATE_4 || null,
                    USERDEFINEDDATE_5: profileDefaults.UserDefinedDate5 || prevFields.USERDEFINEDDATE_5 || null,
                    USERDEFINEDDATE_6: profileDefaults.UserDefinedDate6 || prevFields.USERDEFINEDDATE_6 || null,
                    USERDEFINEDDATE_7: profileDefaults.UserDefinedDate7 || prevFields.USERDEFINEDDATE_7 || null,
                    USERDEFINEDDATE_8: profileDefaults.UserDefinedDate8 || prevFields.USERDEFINEDDATE_8 || null,
                    USERDEFINEDDATE_9: profileDefaults.UserDefinedDate9 || prevFields.USERDEFINEDDATE_9 || null,
                    USERDEFINEDDATE_10: profileDefaults.UserDefinedDate10 || prevFields.USERDEFINEDDATE_10 || null,
                    USERDEFINEDREFERENCE_1: profileDefaults.UserDefinedReference1 || prevFields.USERDEFINEDREFERENCE_1 || '',
                    USERDEFINEDREFERENCE_2: profileDefaults.UserDefinedReference2 || prevFields.USERDEFINEDREFERENCE_2 || '',
                    USERDEFINEDREFERENCE_3: profileDefaults.UserDefinedReference3 || prevFields.USERDEFINEDREFERENCE_3 || '',
                    USERDEFINEDREFERENCE_4: profileDefaults.UserDefinedReference4 || prevFields.USERDEFINEDREFERENCE_4 || '',
                    USERDEFINEDREFERENCE_5: profileDefaults.UserDefinedReference5 || prevFields.USERDEFINEDREFERENCE_5 || '',
                    USERDEFINEDREFERENCE_6: profileDefaults.UserDefinedReference6 || prevFields.USERDEFINEDREFERENCE_6 || '',
                    USERDEFINEDREFERENCE_7: profileDefaults.UserDefinedReference7 || prevFields.USERDEFINEDREFERENCE_7 || '',
                    USERDEFINEDREFERENCE_8: profileDefaults.UserDefinedReference8 || prevFields.USERDEFINEDREFERENCE_8 || '',
                    USERDEFINEDREFERENCE_9: profileDefaults.UserDefinedReference9 || prevFields.USERDEFINEDREFERENCE_9 || '',
                    USERDEFINEDREFERENCE_10: profileDefaults.UserDefinedReference10 || prevFields.USERDEFINEDREFERENCE_10 || '',
                    USERDEFINEDQUANTITY_1: profileDefaults.UserDefinedQuantity1 || prevFields.USERDEFINEDQUANTITY_1 || null,
                    USERDEFINEDQUANTITY_2: profileDefaults.UserDefinedQuantity2 || prevFields.USERDEFINEDQUANTITY_2 || null,
                    USERDEFINEDQUANTITY_3: profileDefaults.UserDefinedQuantity3 || prevFields.USERDEFINEDQUANTITY_3 || null,
                    USERDEFINEDQUANTITY_4: profileDefaults.UserDefinedQuantity4 || prevFields.USERDEFINEDQUANTITY_4 || null,
                    USERDEFINEDQUANTITY_5: profileDefaults.UserDefinedQuantity5 || prevFields.USERDEFINEDQUANTITY_5 || null,
                    USERDEFINEDQUANTITY_6: profileDefaults.UserDefinedQuantity6 || prevFields.USERDEFINEDQUANTITY_6 || null,
                    USERDEFINEDQUANTITY_7: profileDefaults.UserDefinedQuantity7 || prevFields.USERDEFINEDQUANTITY_7 || null,
                    USERDEFINEDQUANTITY_8: profileDefaults.UserDefinedQuantity8 || prevFields.USERDEFINEDQUANTITY_8 || null,
                    USERDEFINEDQUANTITY_9: profileDefaults.UserDefinedQuantity9 || prevFields.USERDEFINEDQUANTITY_9 || null,
                    USERDEFINEDQUANTITY_10: profileDefaults.UserDefinedQuantity10 || prevFields.USERDEFINEDQUANTITY_10 || null,
                    USERDEFINEDCOMMENT: profileDefaults.UserDefinedComment || prevFields.USERDEFINEDCOMMENT || '',
                    R_GLACCOUNT: profileDefaults.RGlAccount || prevFields.R_GLACCOUNT || null,
                    R_GLDEPARTMENT: profileDefaults.RGlDepartment || prevFields.R_GLDEPARTMENT || null,
                    R_FAFUND: profileDefaults.RFaFund || prevFields.R_FAFUND || null,
                    R_CAJOB: profileDefaults.RCaJob || prevFields.R_CAJOB || null,
                    R_CAACTIVITY: profileDefaults.RCaActivity || prevFields.R_CAACTIVITY || null,
                    MANAGEMENTSOURCE: profileDefaults.ManagementSource || prevFields.MANAGEMENTSOURCE || 'C',
                    STORAGETIMING: profileDefaults.StorageTiming || prevFields.STORAGETIMING || '',
                    STORAGEFREQUENCY: profileDefaults.StorageFrequency || prevFields.STORAGEFREQUENCY || '',
                    STATUS: profileDefaults.Status || prevFields.STATUS || 'A',
                    TAXABLE: profileDefaults.Taxable || prevFields.TAXABLE || 'N',
                    DISCOUNTABLE: profileDefaults.Discountable || prevFields.DISCOUNTABLE || 'N',
                    BILLINGMETHOD: profileDefaults.BillingMethod || prevFields.BILLINGMETHOD || 'I',
                    CHARGEBASISUNITS: profileDefaults.ChargeBasisUnits || prevFields.CHARGEBASISUNITS || 'I',
                    ROUNDING: profileDefaults.Rounding || prevFields.ROUNDING || 'N',
                    ACCESSBILLAS: profileDefaults.AccessBillAs || prevFields.ACCESSBILLAS || 'Y',
                    BILLASMINIMUMQUANTITY: profileDefaults.MinimumBillAsQuantity || prevFields.BILLASMINIMUMQUANTITY || null,
                    CHARGEBASISLEVEL: profileDefaults.ChargeBasisLevel || prevFields.CHARGEBASISLEVEL || 'I',
                    CHARGEUOM: profileDefaults.ChargeUom || prevFields.CHARGEUOM || null,
                    RATESOURCE: profileDefaults.RateSource || prevFields.RATESOURCE || 'S',
                    UNITCHARGERATE: profileDefaults.UnitChargeRate || prevFields.UNITCHARGERATE || null,
                    R_RATETABLE: profileDefaults.RRateTable || prevFields.R_RATETABLE || null,
                    RATETABLEQUANTITYBASIS: profileDefaults.RateTableQuantityBasis || prevFields.RATETABLEQUANTITYBASIS || 'I',
                    CUMULATIVERATE: profileDefaults.CumulativeRate || prevFields.CUMULATIVERATE || '',
                    UNITCHARGEFACTOR: profileDefaults.UnitChargeFactor || prevFields.UNITCHARGEFACTOR || null,
                    MINIMUMCHARGEAMOUNT: profileDefaults.MinimumChargeAmount || prevFields.MINIMUMCHARGEAMOUNT || null,
                    MAXIMUMCHARGEAMOUNT: profileDefaults.MaximumChargeAmount || prevFields.MAXIMUMCHARGEAMOUNT || null
                }));
            }
        } catch (error) {
            console.error('Error loading profile defaults:', error);
            // Silently fail - profile defaults are optional
        }
    }, [accessToken]);

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => {
            const updatedFields = {
                ...prevFields,
                [name]: value
            };

            // If R_PROFILE changed and we're creating a new record, load profile defaults.
            // Never on the profile-defaults route: that path edits the profile's OWN template, and
            // loadProfileDefaults copies a profile's values onto a different, new charge code.
            if (name === 'R_PROFILE' && !readProfileDefaults().isProfileDefaults &&
                (!prevFields.ROWID || prevFields.ROWID === '00000000-0000-0000-0000-000000000000')) {
                // Load profile defaults asynchronously
                if (value && value !== '00000000-0000-0000-0000-000000000000') {
                    loadProfileDefaults(value);
                }
            }

            return updatedFields;
        });
    }, [loadProfileDefaults]);

    const loadChargeCodeById = useCallback(async (rowId) => {
        if (!rowId || rowId === '00000000-0000-0000-0000-000000000000') {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.ChargeCode.GetChargeCode}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                const chargeCode = response.data;
                setformFields({
                    ROWID: chargeCode.RowId || initialFormFields.ROWID,
                    RECORD: chargeCode.RecordNumber || '',
                    // Carried so an update posts it back. Without it the saved 'P' template loaded
                    // as a blank record type, and the API then applied the ordinary charge-code
                    // rules to it - the profile's own template would demand a charge code on save.
                    // It is also the fallback readProfileDefaults uses when the URL has no
                    // recordType, so a 'P' record reached any other way still behaves as one.
                    RECORDTYPE: chargeCode.RecordType || '',
                    R_PROFILE: chargeCode.RProfileCode || '',
                    CHARGECODE: chargeCode.ChargeCode || '',
                    DESCRIPTION1: chargeCode.Description1 || '',
                    DESCRIPTION2: chargeCode.Description2 || '',
                    CHARGETYPE: chargeCode.ChargeType || '',
                    MANAGEMENTSOURCE: chargeCode.ManagementSource || '',
                    revenueGroup: chargeCode.RevenueGroup || '',
                    COMMENT: chargeCode.Comment || '',
                    USERDEFINEDCODE_1: chargeCode.UserDefinedCode1 || '',
                    USERDEFINEDCODE_2: chargeCode.UserDefinedCode2 || '',
                    USERDEFINEDCODE_3: chargeCode.UserDefinedCode3 || '',
                    USERDEFINEDCODE_4: chargeCode.UserDefinedCode4 || '',
                    USERDEFINEDCODE_5: chargeCode.UserDefinedCode5 || '',
                    USERDEFINEDCODE_6: chargeCode.UserDefinedCode6 || '',
                    USERDEFINEDCODE_7: chargeCode.UserDefinedCode7 || '',
                    USERDEFINEDCODE_8: chargeCode.UserDefinedCode8 || '',
                    USERDEFINEDCODE_9: chargeCode.UserDefinedCode9 || '',
                    USERDEFINEDCODE_10: chargeCode.UserDefinedCode10 || '',
                    USERDEFINEDDATE_1: chargeCode.UserDefinedDate1 || null,
                    USERDEFINEDDATE_2: chargeCode.UserDefinedDate2 || null,
                    USERDEFINEDDATE_3: chargeCode.UserDefinedDate3 || null,
                    USERDEFINEDDATE_4: chargeCode.UserDefinedDate4 || null,
                    USERDEFINEDDATE_5: chargeCode.UserDefinedDate5 || null,
                    USERDEFINEDDATE_6: chargeCode.UserDefinedDate6 || null,
                    USERDEFINEDDATE_7: chargeCode.UserDefinedDate7 || null,
                    USERDEFINEDDATE_8: chargeCode.UserDefinedDate8 || null,
                    USERDEFINEDDATE_9: chargeCode.UserDefinedDate9 || null,
                    USERDEFINEDDATE_10: chargeCode.UserDefinedDate10 || null,
                    USERDEFINEDREFERENCE_1: chargeCode.UserDefinedReference1 || '',
                    USERDEFINEDREFERENCE_2: chargeCode.UserDefinedReference2 || '',
                    USERDEFINEDREFERENCE_3: chargeCode.UserDefinedReference3 || '',
                    USERDEFINEDREFERENCE_4: chargeCode.UserDefinedReference4 || '',
                    USERDEFINEDREFERENCE_5: chargeCode.UserDefinedReference5 || '',
                    USERDEFINEDREFERENCE_6: chargeCode.UserDefinedReference6 || '',
                    USERDEFINEDREFERENCE_7: chargeCode.UserDefinedReference7 || '',
                    USERDEFINEDREFERENCE_8: chargeCode.UserDefinedReference8 || '',
                    USERDEFINEDREFERENCE_9: chargeCode.UserDefinedReference9 || '',
                    USERDEFINEDREFERENCE_10: chargeCode.UserDefinedReference10 || '',
                    USERDEFINEDQUANTITY_1: chargeCode.UserDefinedQuantity1 || null,
                    USERDEFINEDQUANTITY_2: chargeCode.UserDefinedQuantity2 || null,
                    USERDEFINEDQUANTITY_3: chargeCode.UserDefinedQuantity3 || null,
                    USERDEFINEDQUANTITY_4: chargeCode.UserDefinedQuantity4 || null,
                    USERDEFINEDQUANTITY_5: chargeCode.UserDefinedQuantity5 || null,
                    USERDEFINEDQUANTITY_6: chargeCode.UserDefinedQuantity6 || null,
                    USERDEFINEDQUANTITY_7: chargeCode.UserDefinedQuantity7 || null,
                    USERDEFINEDQUANTITY_8: chargeCode.UserDefinedQuantity8 || null,
                    USERDEFINEDQUANTITY_9: chargeCode.UserDefinedQuantity9 || null,
                    USERDEFINEDQUANTITY_10: chargeCode.UserDefinedQuantity10 || null,
                    USERDEFINEDCOMMENT: chargeCode.UserDefinedComment || '',
                    R_GLACCOUNT: chargeCode.RGlAccount || null,
                    R_GLDEPARTMENT: chargeCode.RGlDepartment || null,
                    R_FAFUND: chargeCode.RFaFund || null,
                    R_CAJOB: chargeCode.RCaJob || null,
                    R_CAACTIVITY: chargeCode.RCaActivity || null,
                    STORAGETIMING: chargeCode.StorageTiming || '',
                    STORAGEFREQUENCY: chargeCode.StorageFrequency || '',
                    STATUS: chargeCode.Status || '',
                    TAXABLE: chargeCode.Taxable || '',
                    DISCOUNTABLE: chargeCode.Discountable || '',
                    BILLINGMETHOD: chargeCode.BillingMethod || '',
                    CHARGEBASISUNITS: chargeCode.ChargeBasisUnits || '',
                    ROUNDING: chargeCode.Rounding || '',
                    ACCESSBILLAS: chargeCode.AccessBillAs || '',
                    BILLASMINIMUMQUANTITY: chargeCode.MinimumBillAsQuantity || null,
                    CHARGEBASISLEVEL: chargeCode.ChargeBasisLevel || '',
                    CHARGEUOM: chargeCode.ChargeUom || null,
                    RATESOURCE: chargeCode.RateSource || '',
                    UNITCHARGERATE: chargeCode.UnitChargeRate || null,
                    R_RATETABLE: chargeCode.RRateTable || null,
                    RATETABLEQUANTITYBASIS: chargeCode.RateTableQuantityBasis || '',
                    CUMULATIVERATE: chargeCode.CumulativeRate || '',
                    UNITCHARGEFACTOR: chargeCode.UnitChargeFactor || null,
                    MINIMUMCHARGEAMOUNT: chargeCode.MinimumChargeAmount || null,
                    MAXIMUMCHARGEAMOUNT: chargeCode.MaximumChargeAmount || null
                });

                // The record exists now, so the profile is fixed (Delphi: "not AddMode"). Re-applied
                // here because the form-load hook ran before this fetch returned.
                DisableProfileField();
            }
        } catch (error) {
            console.error('Error loading Charge Code:', error);
            notify('Failed to load Charge Code data.', 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadChargeCodeById(rowId);
        }
    }, [props.id, loadChargeCodeById]);

    /**
     * Same shape as itemDataEntry/Form.js: the parent owns formRefs, so the parent owns the
     * enable/disable rules. These run through ref.instance().option('disabled', ...) rather than the
     * layout's readOnly flag, because DynamicFromLookup binds readOnly as a React prop - DevExtreme
     * re-applies it from props on every render and a schema change made after mount is lost.
     */
    const setFieldDisabled = (name, disabled) => {
        const ref = formRefs.current?.[name];
        if (ref && typeof ref.instance === 'function') {
            ref.instance().option('disabled', disabled);
        }
    };

    // Delphi disables edtP_PROFILE in two places, and between them they cover every path except a
    // normal add: uWMD0001.pas:562 for a record that already exists ("(not AddMode) and
    // RecordsExist"), and SetParameters for the template the Charge Profile screen opens. On a normal
    // add it stays enabled - that is where picking a profile seeds the new charge code's defaults.
    const DisableProfileField = () => setFieldDisabled('R_PROFILE', true);

    /**
     * Called from PrimaryTab's onFormLoad, which is the first point at which FormField has created
     * the widgets and registered them in formRefs. Record # is KEYSEQUENCE, assigned by the database
     * and shown through a display control in Delphi, so it is never editable - on add or on edit.
     *
     * Covers all three paths on its own, so it can safely be called again at any time. The record
     * load calls it as well, because on a normal edit the fetch returns after this has already run.
     */
    const primaryFormLookupsLoad = () => {
        setFieldDisabled('RECORD', true);

        // formFieldsRef, not formFields: this runs from a callback captured on an earlier render, so
        // the state variable would be stale exactly when the record has just loaded.
        const recordExists = !!(formFieldsRef.current?.ROWID && formFieldsRef.current.ROWID !== EMPTY_GUID);

        if (recordExists || readProfileDefaults().isProfileDefaults) {
            DisableProfileField();
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsFormSubmitted(true);
        setLoadPanelVisible(true);

        try {
            const payload = {
                RowId: formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000" ? formFields.ROWID : "00000000-0000-0000-0000-000000000000",
                KeySequence: 0, // Backend will generate for new records or use existing for updates
                RProfileCode: formFields.R_PROFILE || null,

                // Tells the API this is the profile's template, which exempts it from the

                // charge-code required and duplicate rules (Delphi NonBlankFields.Clear).

                RecordType: formFields.RECORDTYPE || null,
                ChargeCode: formFields.CHARGECODE || "",
                Description1: formFields.DESCRIPTION1 || "",
                Description2: formFields.DESCRIPTION2 || "",
                ChargeType: formFields.CHARGETYPE || "",
                ManagementSource: formFields.MANAGEMENTSOURCE || "",
                RevenueGroup: formFields.revenueGroup || "",
                Comment: formFields.COMMENT || "",
                UserDefinedCode1: formFields.USERDEFINEDCODE_1 || "",
                UserDefinedCode2: formFields.USERDEFINEDCODE_2 || "",
                UserDefinedCode3: formFields.USERDEFINEDCODE_3 || "",
                UserDefinedCode4: formFields.USERDEFINEDCODE_4 || "",
                UserDefinedCode5: formFields.USERDEFINEDCODE_5 || "",
                UserDefinedCode6: formFields.USERDEFINEDCODE_6 || "",
                UserDefinedCode7: formFields.USERDEFINEDCODE_7 || "",
                UserDefinedCode8: formFields.USERDEFINEDCODE_8 || "",
                UserDefinedCode9: formFields.USERDEFINEDCODE_9 || "",
                UserDefinedCode10: formFields.USERDEFINEDCODE_10 || "",
                UserDefinedDate1: formFields.USERDEFINEDDATE_1 || null,
                UserDefinedDate2: formFields.USERDEFINEDDATE_2 || null,
                UserDefinedDate3: formFields.USERDEFINEDDATE_3 || null,
                UserDefinedDate4: formFields.USERDEFINEDDATE_4 || null,
                UserDefinedDate5: formFields.USERDEFINEDDATE_5 || null,
                UserDefinedDate6: formFields.USERDEFINEDDATE_6 || null,
                UserDefinedDate7: formFields.USERDEFINEDDATE_7 || null,
                UserDefinedDate8: formFields.USERDEFINEDDATE_8 || null,
                UserDefinedDate9: formFields.USERDEFINEDDATE_9 || null,
                UserDefinedDate10: formFields.USERDEFINEDDATE_10 || null,
                UserDefinedReference1: formFields.USERDEFINEDREFERENCE_1 || "",
                UserDefinedReference2: formFields.USERDEFINEDREFERENCE_2 || "",
                UserDefinedReference3: formFields.USERDEFINEDREFERENCE_3 || "",
                UserDefinedReference4: formFields.USERDEFINEDREFERENCE_4 || "",
                UserDefinedReference5: formFields.USERDEFINEDREFERENCE_5 || "",
                UserDefinedReference6: formFields.USERDEFINEDREFERENCE_6 || "",
                UserDefinedReference7: formFields.USERDEFINEDREFERENCE_7 || "",
                UserDefinedReference8: formFields.USERDEFINEDREFERENCE_8 || "",
                UserDefinedReference9: formFields.USERDEFINEDREFERENCE_9 || "",
                UserDefinedReference10: formFields.USERDEFINEDREFERENCE_10 || "",
                UserDefinedQuantity1: formFields.USERDEFINEDQUANTITY_1 || null,
                UserDefinedQuantity2: formFields.USERDEFINEDQUANTITY_2 || null,
                UserDefinedQuantity3: formFields.USERDEFINEDQUANTITY_3 || null,
                UserDefinedQuantity4: formFields.USERDEFINEDQUANTITY_4 || null,
                UserDefinedQuantity5: formFields.USERDEFINEDQUANTITY_5 || null,
                UserDefinedQuantity6: formFields.USERDEFINEDQUANTITY_6 || null,
                UserDefinedQuantity7: formFields.USERDEFINEDQUANTITY_7 || null,
                UserDefinedQuantity8: formFields.USERDEFINEDQUANTITY_8 || null,
                UserDefinedQuantity9: formFields.USERDEFINEDQUANTITY_9 || null,
                UserDefinedQuantity10: formFields.USERDEFINEDQUANTITY_10 || null,
                UserDefinedComment: formFields.USERDEFINEDCOMMENT || "",
                RGlAccount: formFields.R_GLACCOUNT || null,
                RGlDepartment: formFields.R_GLDEPARTMENT || null,
                RFaFund: formFields.R_FAFUND || null,
                RCaJob: formFields.R_CAJOB || null,
                RCaActivity: formFields.R_CAACTIVITY || null,
                StorageTiming: formFields.STORAGETIMING || "",
                StorageFrequency: formFields.STORAGEFREQUENCY || "",
                Status: formFields.STATUS || "",
                Taxable: formFields.TAXABLE || "",
                Discountable: formFields.DISCOUNTABLE || "",
                BillingMethod: formFields.BILLINGMETHOD || "",
                ChargeBasisUnits: formFields.CHARGEBASISUNITS || "",
                Rounding: formFields.ROUNDING || "",
                AccessBillAs: formFields.ACCESSBILLAS || "",
                MinimumBillAsQuantity: formFields.BILLASMINIMUMQUANTITY || null,
                ChargeBasisLevel: formFields.CHARGEBASISLEVEL || "",
                ChargeUom: formFields.CHARGEUOM || null,
                RateSource: formFields.RATESOURCE || "",
                UnitChargeRate: formFields.UNITCHARGERATE || null,
                RRateTable: formFields.R_RATETABLE || null,
                RateTableQuantityBasis: formFields.RATETABLEQUANTITYBASIS || "",
                CumulativeRate: formFields.CUMULATIVERATE || "",
                UnitChargeFactor: formFields.UNITCHARGEFACTOR || null,
                MinimumChargeAmount: formFields.MINIMUMCHARGEAMOUNT || null,
                MaximumChargeAmount: formFields.MAXIMUMCHARGEAMOUNT || null
            };

            // Determine if this is an insert or update based on ROWID
            const isUpdate = formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000";
            const endpoint = isUpdate ? data.ChargeCode.UpdateChargeCode : data.ChargeCode.InsertChargeCode;
            const method = isUpdate ? 'put' : 'post';
            
            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.RowId || prev.ROWID,
                    // Show formatted record number (derived from KeySequence on backend)
                    RECORD: saved.RecordNumber || prev.RECORD,
                }));
            }

            notify("Charge Code saved successfully.", "success", 3000);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                err?.message ||
                "Failed to save Charge Code.";

            notify(message, "error", 5000);
        } finally {
            hideLoadPanel();
            setIsFormSubmitted(false);
        }
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const DeleteClick = () => {
        if (!formFields.ROWID || formFields.ROWID === "00000000-0000-0000-0000-000000000000") {
            notify("No record selected for deletion.", "warning", 3000);
            return;
        }

        let result = confirm("<i>Are you sure you want to delete this Charge Code?</i>", "Delete");
        result.then(async (dialogResult) => {
            if (dialogResult) {
                setLoadPanelVisible(true);
                try {
                    const url = `${window._env_.REACT_APP_APIS}/${data.ChargeCode.DeleteChargeCode}`;
                    const response = await axios.post(url, formFields.ROWID, {
                        headers: {
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.data && response.data.Success) {
                        notify("Charge Code deleted successfully.", "success", 3000);
                        // Reset form and navigate back to list
                        setformFields(initialFormFields);
                        setTimeout(() => {
                            history.push("/wm/codes/charge-code");
                        }, 1000);
                    } else {
                        notify(response.data?.message || "Failed to delete Charge Code.", "error", 3000);
                    }
                } catch (error) {
                    const message = error?.response?.data?.message || error?.message || "An error occurred while deleting the Charge Code.";
                    notify(message, "error", 5000);
                } finally {
                    setLoadPanelVisible(false);
                }
            }
        });
    };

    const handleReset = () => {
        // props.id is null on the add route even after a save - the URL never changes - so fall back
        // to the key the insert response put in state, or Reset would discard the saved record.
        const savedRowId = formFields.ROWID && formFields.ROWID !== EMPTY_GUID ? formFields.ROWID : null;
        const rowId = props.id || savedRowId;
        if (rowId && rowId !== EMPTY_GUID) {
            loadChargeCodeById(rowId);
        } else {
            setformFields(initialFormFields);

            // The profile owns this record, so a reset must not clear its pointer or unlock the
            // field. Re-seeding after the blank works because SeedProfileDefaultsCharge uses a
            // functional update: React applies it to the result of the reset in the same batch.
            const { isProfileDefaults: isProfileDefaultsRoute, profileRowId } = readProfileDefaults();
            if (isProfileDefaultsRoute) {
                if (profileRowId) {
                    SeedProfileDefaultsCharge(profileRowId);
                }

                DisableProfileField();
            }
        }
    };

    /**
     * uWMD0001.SetParameters' seed branch - "if not RecordsExist then Insert; P_ProfileCode :=
     * FP_Profile; Post". Nothing else is stamped, exactly as Delphi leaves it: no charge code, no
     * description. The profile key arrives in the URL, so there is no lookup here.
     */
    const SeedProfileDefaultsCharge = (profileRowId) => {
        setformFields((prev) => ({
            ...prev,
            ROWID: EMPTY_GUID,
            RECORDTYPE: RECORD_TYPE.PROFILE_DEFAULTS,
            R_PROFILE: profileRowId,
        }));
    };

    // Seed once, and only when creating: props.id is the create/edit discriminator. Relying on
    // RProfile being absent on the edit route would be fragile, because that route carries it too
    // (so Back can find its way home).
    useEffect(() => {
        const { isProfileDefaults: isProfileDefaultsRoute, profileRowId } = readProfileDefaults();
        if (!isProfileDefaultsRoute || props.id || hasSeededProfileDefaults.current || !profileRowId) {
            return;
        }

        SeedProfileDefaultsCharge(profileRowId);
        hasSeededProfileDefaults.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    // The profile owns this record, so its Profile field stays locked for the whole session
    // (Delphi disables the profile code field in SetParameters). Re-asserted on every render
    // because the widget is created by the dynamic form after the first pass.
    useEffect(() => {
        if (!isProfileDefaults) return;

        const ref = formRefs.current?.['R_PROFILE'];
        if (ref && typeof ref.instance === 'function') {
            ref.instance().option('disabled', true);
        }
    });

    useEffect(() => {
        formFieldsRef.current = formFields;
    }, [formFields]);

    const goBack = () => {
        // Opened from the Charge Profile screen, so Back returns there rather than to the charge
        // listing - Delphi returns to the calling form (ab_ExitProgram).
        //
        // Push the profile's Edit route explicitly rather than history.goBack(): the profile form
        // stays on its Create URL after a save (it only updates ROWID in state), so goBack() would
        // remount a blank Add Profile screen and the saved record would appear lost. The key comes
        // from the URL, which the Charge Profile screen stamps on both of its Charges Defaults
        // routes; formFields.R_PROFILE is only a fallback.
        if (isProfileDefaults) {
            const { profileRowId } = readProfileDefaults();
            const target = profileRowId || formFields.R_PROFILE;
            history.push(target ? `${ChargeProfileRoutes.Edit}?rowid=${target}` : ChargeProfileRoutes.List);
            return;
        }

        history.push("/wm/codes/charge-code");
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px'
                                            }}
                                        >
                                            Charge Code
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        disabled={isFormSubmitted}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                {/* Delphi strips abAdd from the navigator for the profile template
                                    (uWMD0001.SetParameters): the profile owns exactly one template,
                                    so there is nothing to add from here. Hidden rather than
                                    disabled, matching the navigator button simply not being there. */}
                                {!isProfileDefaults && (
                                    <Item location="after">
                                        <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                    </Item>
                                )}
                                <Item location="after">
                                    <Button className="accordianButton" onClick={DeleteClick} text="Delete" disabled={formFields.ROWID === "00000000-0000-0000-0000-000000000000"} type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab 
                                        formFields={formFields} 
                                        setformFields={setformFields} 
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                        isProfileDefaults={isProfileDefaults}
                                        primaryFormLookupsLoad={primaryFormLookupsLoad}
                                    />
                                </TabItem>
                                <TabItem title="Billing">
                                    <BillingTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="User Defined">
                                    <UserDefinedTab 
                                        formFields={formFields} 
                                        setformFields={setformFields} 
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Accounting">
                                    <AccountingTab 
                                        formFields={formFields} 
                                        setformFields={setformFields} 
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                        isProfileDefaults={isProfileDefaults}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);

